/**
 * Casca da aplicação: navegação lateral, barra superior e transição entre telas.
 *
 * As rotas são reais — o protótipo navega de verdade entre telas, e não simula
 * transição. É o que permite avaliar se o caminho entre as telas faz sentido,
 * que uma sequência de imagens não mostraria.
 *
 * A navegação é a `Sidebar` da Kumo: fixa ao lado no desktop, recolhível a ícones, e
 * gaveta no celular — o menu horizontal anterior simplesmente não cabia em tela estreita.
 */

import { motion } from "motion/react";
import { Button, Sidebar, useSidebar } from "@cloudflare/kumo";
import {
  ChartLineIcon,
  CpuIcon,
  FlowArrowIcon,
  GaugeIcon,
  ListBulletsIcon,
  MoonIcon,
  PulseIcon,
  SealCheckIcon,
  SunIcon,
  TrendUpIcon,
  type Icon,
} from "@phosphor-icons/react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useThemeMode } from "./components/ThemeMode.js";
import { activeNavPath, isScenarioDetail } from "./lib/navigation.js";
import { Advisor } from "./screens/Advisor.js";
import { ChatCheck } from "./screens/ChatCheck.js";
import { Compare } from "./screens/Compare.js";
import { Hardware } from "./screens/Hardware.js";
import { Live } from "./screens/Live.js";
import { Pipeline } from "./screens/Pipeline.js";
import { Runs } from "./screens/Runs.js";
import { Scale } from "./screens/Scale.js";
import { ScenarioDetail } from "./screens/ScenarioDetail.js";

/** Uma entrada da navegação, com a pergunta que a tela responde. */
interface NavItem {
  readonly path: string;
  readonly label: string;
  readonly icon: Icon;
  readonly question: string;
}

/** Um grupo de entradas da navegação. */
interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

/**
 * Navegação em dois grupos, na ordem de uso esperada.
 *
 * "Medição" é o dia a dia da bancada; "Decisão" é o que alimenta a compra. Separar os
 * dois diz a quem chega onde está a resposta que ele procura.
 */
const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: "Medição",
    items: [
      { path: "/chat-check", label: "Verificação rápida", icon: GaugeIcon, question: "está atingindo o alvo?" },
      { path: "/live", label: "Ao vivo", icon: PulseIcon, question: "como está a execução em curso?" },
      { path: "/runs", label: "Execuções", icon: ListBulletsIcon, question: "o que já foi medido?" },
      { path: "/compare", label: "Comparar", icon: ChartLineIcon, question: "onde cada hardware deixa de atender?" },
    ],
  },
  {
    label: "Decisão",
    items: [
      { path: "/scale", label: "Escala", icon: TrendUpIcon, question: "quantos usuários cabem, e a que custo?" },
      { path: "/hardware", label: "Hardware", icon: CpuIcon, question: "o que foi detectado, e quanto consome?" },
      { path: "/pipeline", label: "Cadeia", icon: FlowArrowIcon, question: "onde o tempo até o 1º token é gasto?" },
      { path: "/advisor", label: "Recomendação", icon: SealCheckIcon, question: "o que comprar?" },
    ],
  },
];

/** Todas as entradas, achatadas, para achar o título da tela corrente. */
const NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

/** Título e pergunta da barra superior para a rota corrente. */
function pageHeading(pathname: string): { title: string; question: string } {
  if (isScenarioDetail(pathname)) {
    return { title: "Detalhe do cenário", question: "como a latência se distribui?" };
  }
  const item = NAV_ITEMS.find((entry) => entry.path === pathname);
  return item === undefined ? { title: "benchboard", question: "" } : { title: item.label, question: item.question };
}

/**
 * Casca dentro do provedor da `Sidebar` — `useSidebar` só existe ali dentro.
 *
 * No celular a navegação é gaveta, e a gaveta precisa fechar ao escolher a tela; sem
 * isso, o toque navega e a tela nova fica escondida atrás do menu.
 */
function Shell(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const { isDark, toggle } = useThemeMode();
  const activePath = activeNavPath(location.pathname);
  const heading = pageHeading(location.pathname);

  /** Vai para a tela e, no celular, recolhe a gaveta. */
  const goTo = (path: string): void => {
    navigate(path);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <>
      {/* `sticky` + `h-dvh`, SÓ no desktop: a linha flex do provedor não tem altura definida,
          e o `h-full` da Sidebar resolvia para a altura dos itens — o fundo parava no meio da
          janela. No celular a Kumo desenha a gaveta como `fixed`, e o `sticky` passava por
          cima: a gaveta escondida voltava a ocupar 260 px na linha (os dois medidos em
          15/09/2026). O corte usa o `isMobile` da própria Kumo, e não um breakpoint nosso. */}
      <Sidebar {...(isMobile ? {} : { className: "sticky top-0 h-dvh self-start" })}>
        <Sidebar.Header>
          <div className="flex flex-col px-2 py-1">
            <span className="text-lg font-semibold tracking-tight text-kumo-strong">benchboard</span>
            <span className="text-xs text-kumo-subtle">protótipo · dados sintéticos</span>
          </div>
        </Sidebar.Header>
        <Sidebar.Content>
          {NAV_GROUPS.map((group) => (
            <Sidebar.Group key={group.label}>
              <Sidebar.GroupLabel>{group.label}</Sidebar.GroupLabel>
              <Sidebar.Menu>
                {group.items.map((item) => (
                  <Sidebar.MenuButton
                    key={item.path}
                    icon={item.icon}
                    active={activePath === item.path}
                    tooltip={item.label}
                    onClick={() => goTo(item.path)}
                  >
                    {item.label}
                  </Sidebar.MenuButton>
                ))}
              </Sidebar.Menu>
            </Sidebar.Group>
          ))}
        </Sidebar.Content>
      </Sidebar>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-kumo-line bg-kumo-canvas/85 px-4 py-3 backdrop-blur sm:px-6">
          <Sidebar.Trigger aria-label="Abrir ou recolher a navegação" />
          {/* Só entrada, sem saída animada: com `AnimatePresence mode="wait"` o conteúdo novo
              esperava a saída do antigo terminar, e com a animação parada (aba em segundo
              plano) a rota trocava e a tela antiga ficava presa (medido em 15/09/2026). */}
          <motion.div
            key={heading.title}
            className="min-w-0 flex-1"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <h1 className="truncate text-lg font-semibold text-kumo-strong sm:text-xl">{heading.title}</h1>
            {heading.question !== "" && (
              <p className="hidden truncate text-sm text-kumo-subtle sm:block">{heading.question}</p>
            )}
          </motion.div>
          <Button
            variant="ghost"
            shape="square"
            icon={isDark ? SunIcon : MoonIcon}
            aria-label={isDark ? "Mudar para o modo claro" : "Mudar para o modo escuro"}
            onClick={toggle}
          />
        </header>

        <main className="mx-auto w-full max-w-[1480px] flex-1 px-4 pt-5 pb-10 sm:px-6 lg:px-8">
          {/* Mesma decisão do título: a tela nova entra animada, mas não espera a saída da
              anterior — a troca de conteúdo acompanha a rota na hora. */}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <Routes location={location}>
              <Route path="/" element={<Navigate to="/chat-check" replace />} />
              <Route path="/chat-check" element={<ChatCheck />} />
              <Route path="/live" element={<Live />} />
              <Route path="/runs" element={<Runs />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/scale" element={<Scale />} />
              <Route path="/hardware" element={<Hardware />} />
              <Route path="/pipeline" element={<Pipeline />} />
              <Route path="/advisor" element={<Advisor />} />
              <Route path="/scenario/:id" element={<ScenarioDetail />} />
            </Routes>
          </motion.div>
        </main>
      </div>
    </>
  );
}

/** Raiz da aplicação: provedor da navegação lateral em volta da casca. */
export function App(): React.ReactElement {
  return (
    <Sidebar.Provider defaultOpen collapsible="icon" className="min-h-dvh">
      <Shell />
    </Sidebar.Provider>
  );
}
